import { computed } from 'vue'
import type { Ref } from 'vue'

interface TableCell {
  content: string
  align?: 'left' | 'center' | 'right'
}

interface TableData {
  headers: string[]
  rows: string[][]
  alignments: ('left' | 'center' | 'right')[]
}

export function useStreamingTable() {
  function parseTableAlignment(separatorRow: string): ('left' | 'center' | 'right')[] {
    const cells = separatorRow.split('|').map(cell => cell.trim()).filter(Boolean)
    return cells.map((cell) => {
      if (cell.startsWith(':') && cell.endsWith(':'))
        return 'center'
      if (cell.endsWith(':'))
        return 'right'
      return 'left'
    })
  }

  function parseTableRow(row: string, alignments: ('left' | 'center' | 'right')[] = []): TableCell[] {
    const cells = row.split('|').map(cell => cell.trim()).filter(Boolean)
    return cells.map((content, index) => ({
      content,
      align: alignments[index] || 'left',
    }))
  }

  function isTableSeparator(line: string): boolean {
    const trimmed = line.trim()
    return /^[-:\s|]+$/.test(trimmed) && trimmed.includes('-')
  }

  function parseStreamingTable(content: string): {
    isTable: boolean
    tableData?: TableData
    remainingContent?: string
  } {
    const lines = content.split('\n')
    let tableStart = -1
    let tableEnd = -1

    // Find table boundaries
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()
      if (line.includes('|') && line.length > 0) {
        if (tableStart === -1)
          tableStart = i

        tableEnd = i
      }
      else if (tableStart !== -1) {
        // End of table found
        break
      }
    }

    if (tableStart === -1)
      return { isTable: false }

    const tableLines = lines.slice(tableStart, tableEnd + 1)

    // Check if we have at least a header and separator
    if (tableLines.length < 2)
      return { isTable: false }

    const headerLine = tableLines[0]
    const possibleSeparator = tableLines[1]

    if (!isTableSeparator(possibleSeparator)) {
      // Incomplete table - treat as partial
      const alignments: ('left' | 'center' | 'right')[] = []
      const headers = parseTableRow(headerLine, alignments).map(cell => cell.content)

      return {
        isTable: true,
        tableData: {
          headers,
          rows: [],
          alignments,
        },
        remainingContent: lines.slice(tableEnd + 1).join('\n'),
      }
    }

    // Parse complete table
    const alignments = parseTableAlignment(possibleSeparator)
    const headers = parseTableRow(headerLine, alignments).map(cell => cell.content)
    const dataRows = tableLines.slice(2).map(row => parseTableRow(row, alignments).map(cell => cell.content))

    return {
      isTable: true,
      tableData: {
        headers,
        rows: dataRows,
        alignments,
      },
      remainingContent: lines.slice(tableEnd + 1).join('\n'),
    }
  }

  function renderTableToHTML(tableData: TableData, isStreaming = false): string {
    const { headers, rows, alignments } = tableData

    const headerCells = headers
      .map((header, index) => {
        const align = alignments[index] || 'left'
        const alignAttr = align !== 'left' ? ` style="text-align: ${align}"` : ''
        return `<th${alignAttr}>${header}</th>`
      })
      .join('')

    const bodyRows = rows
      .map((row) => {
        const cells = row
          .map((cell, index) => {
            const align = alignments[index] || 'left'
            const alignAttr = align !== 'left' ? ` style="text-align: ${align}"` : ''
            return `<td${alignAttr}>${cell}</td>`
          })
          .join('')
        return `<tr>${cells}</tr>`
      })
      .join('')

    const streamingClass = isStreaming ? ' class="streaming-table"' : ''

    return `
      <table${streamingClass}>
        <thead>
          <tr>${headerCells}</tr>
        </thead>
        <tbody>
          ${bodyRows}
        </tbody>
      </table>
    `
  }

  function createStreamingTableParser(content: Ref<string>) {
    return computed(() => {
      const result = parseStreamingTable(content.value)
      if (!result.isTable)
        return null

      return {
        html: renderTableToHTML(result.tableData!, true),
        data: result.tableData,
        remainingContent: result.remainingContent,
      }
    })
  }

  return {
    parseStreamingTable,
    renderTableToHTML,
    createStreamingTableParser,
    parseTableRow,
    isTableSeparator,
  }
}

// Utility functions for table data manipulation (inspired by Streamdown)
export function tableDataToCSV(tableData: TableData): string {
  const csvRows = [
    tableData.headers.map(header => `"${header.replace(/"/g, '""')}"`).join(','),
    ...tableData.rows.map(row =>
      row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','),
    ),
  ]
  return csvRows.join('\n')
}

export function tableDataToMarkdown(tableData: TableData): string {
  const { headers, rows, alignments } = tableData

  const headerRow = `| ${headers.join(' | ')} |`
  const separatorRow = `| ${alignments
    .map((align) => {
      switch (align) {
        case 'center':
          return ':---:'
        case 'right':
          return '---:'
        default:
          return '---'
      }
    })
    .join(' | ')} |`

  const dataRows = rows.map(row => `| ${row.join(' | ')} |`)

  return [headerRow, separatorRow, ...dataRows].join('\n')
}
